import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Collapse } from "@mui/material";
import { Loading } from "src/reusable/LoadingSpinner";
import { useSimilarCRs } from "../services/AutomationRecordServices";
import { useDatasetList } from "src/views/datasets/services/DatasetServices";
import { FaChevronDown, FaChevronRight, FaSearch } from "react-icons/fa";

const SimilarCRItem = ({ record }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        marginBottom: "8px",
        padding: "8px",
        backgroundColor: "#fafafa",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
        data-attr={`cap-ai:card:expand-similar-cr-${record.source_uid}`}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {expanded ? (
            <FaChevronDown size={12} />
          ) : (
            <FaChevronRight size={12} />
          )}
          <span style={{ fontWeight: 500 }}>{record.source_uid}</span>
          <span
            style={{
              backgroundColor: "#e3f2fd",
              padding: "2px 8px",
              borderRadius: "12px",
              fontSize: "12px",
            }}
          >
            {(record.similarity_score * 100).toFixed(1)}% similar
          </span>
        </div>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div
          style={{
            marginTop: "8px",
            paddingTop: "8px",
            borderTop: "1px solid #e0e0e0",
            fontSize: "13px",
          }}
        >
          {Object.entries(record.data || {}).map(([key, value]) => (
            <div
              key={key}
              style={{ marginBottom: "4px", display: "flex", gap: "8px" }}
            >
              <span
                style={{ fontWeight: 500, minWidth: "120px", flexShrink: 0 }}
              >
                {key}:
              </span>
              <span
                style={{
                  color: "#666",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {String(value)}
              </span>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
};

SimilarCRItem.propTypes = {
  record: PropTypes.shape({
    source_uid: PropTypes.string.isRequired,
    similarity_score: PropTypes.number.isRequired,
    ground_truth_value: PropTypes.string,
    data: PropTypes.object.isRequired,
  }).isRequired,
};

const SimilarCRGroup = ({ group }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div style={{ marginBottom: "16px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          padding: "8px",
          backgroundColor: "#f5f5f5",
          borderRadius: "4px",
          marginBottom: "8px",
        }}
        onClick={() => setExpanded(!expanded)}
        data-attr={`cap-ai:card:toggle-similar-cr-group-${group.ground_truth_value}`}
      >
        {expanded ? <FaChevronDown /> : <FaChevronRight />}
        <span style={{ fontWeight: 600 }}>{group.ground_truth_value}</span>
        <span
          style={{
            backgroundColor: "#2196f3",
            color: "white",
            padding: "2px 8px",
            borderRadius: "12px",
            fontSize: "12px",
          }}
        >
          {group.count} records
        </span>
      </div>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <div style={{ paddingLeft: "16px" }}>
          {group.records.map((record, idx) => (
            <SimilarCRItem key={idx} record={record} />
          ))}
        </div>
      </Collapse>
    </div>
  );
};

SimilarCRGroup.propTypes = {
  group: PropTypes.shape({
    ground_truth_value: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    records: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

// Helper function to group results by a field - for client-side rebucketing
const groupResultsByField = (flatResults, outputFieldName) => {
  if (!flatResults || flatResults.length === 0) return [];

  const groupsDict = {};
  for (const record of flatResults) {
    const groundTruthValue = record.data?.[outputFieldName];
    const groupKey =
      groundTruthValue != null ? String(groundTruthValue) : "Unknown";

    if (!groupsDict[groupKey]) {
      groupsDict[groupKey] = [];
    }
    groupsDict[groupKey].push({
      ...record,
      ground_truth_value:
        groundTruthValue != null ? String(groundTruthValue) : null,
    });
  }

  // Convert to array and sort by count descending
  return Object.entries(groupsDict)
    .map(([groundTruthValue, records]) => ({
      ground_truth_value: groundTruthValue,
      count: records.length,
      records: records.sort((a, b) => b.similarity_score - a.similarity_score),
    }))
    .sort((a, b) => b.count - a.count);
};

export const SimilarCRsSection = ({
  automationRecordId,
  groundTruthDatasetId,
  predictions,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState(
    groundTruthDatasetId || null,
  );
  const [selectedOutputField, setSelectedOutputField] = useState("");
  const [maxResults, setMaxResults] = useState(10);
  const [cachedResults, setCachedResults] = useState(null);
  const [cachedSearchQuery, setCachedSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  // Track the params used for the last successful fetch
  const lastFetchParams = useRef(null);

  const { data: datasets, isLoading: datasetsLoading } = useDatasetList();

  const outputFieldOptions =
    predictions?.map((p) => p.model_output_name).filter(Boolean) || [];

  // Set default output field when predictions change
  useEffect(() => {
    if (outputFieldOptions.length > 0 && !selectedOutputField) {
      setSelectedOutputField(outputFieldOptions[0]);
    }
  }, [outputFieldOptions, selectedOutputField]);

  // Query is always enabled once we have valid params, but we control when to use results
  const {
    data: similarCRs,
    isFetching,
    error: similarCRsError,
    refetch,
  } = useSimilarCRs(
    automationRecordId,
    selectedDatasetId,
    selectedOutputField,
    maxResults,
    false, // never auto-fetch; triggered explicitly via refetch()
  );

  // Update cached results when new data arrives after a search
  useEffect(() => {
    if (similarCRs && !isFetching && isSearching) {
      const flatResults = similarCRs.groups?.flatMap((g) => g.records) || [];
      setCachedResults(flatResults);
      setCachedSearchQuery(similarCRs.search_query_used || "");
      setIsSearching(false);
      lastFetchParams.current = {
        datasetId: selectedDatasetId,
        maxResults: maxResults,
      };
    }
  }, [similarCRs, isFetching, isSearching, selectedDatasetId, maxResults]);

  // Regroup cached results when output field changes (client-side rebucketing)
  const displayGroups = useMemo(() => {
    if (!cachedResults || cachedResults.length === 0) return null;
    return groupResultsByField(cachedResults, selectedOutputField);
  }, [cachedResults, selectedOutputField]);

  const handleExpandToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
    if (!isExpanded) {
      if (!selectedDatasetId && groundTruthDatasetId) {
        setSelectedDatasetId(groundTruthDatasetId);
      }
      if (!selectedOutputField && outputFieldOptions.length > 0) {
        setSelectedOutputField(outputFieldOptions[0]);
      }
    }
  }, [
    isExpanded,
    selectedDatasetId,
    groundTruthDatasetId,
    selectedOutputField,
    outputFieldOptions,
  ]);

  const handleFindSimilar = useCallback(() => {
    setIsSearching(true);
    refetch();
  }, [refetch]);

  // Clear cache when dataset or maxResults changes (need new search)
  const handleDatasetChange = useCallback((e) => {
    setSelectedDatasetId(Number(e.target.value));
    setCachedResults(null);
    setCachedSearchQuery("");
  }, []);

  const handleMaxResultsChange = useCallback((e) => {
    setMaxResults(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)));
    setCachedResults(null);
    setCachedSearchQuery("");
  }, []);

  const showLoading = isFetching && isSearching;

  return (
    <div style={{ marginTop: "16px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          cursor: "pointer",
          padding: "8px",
          backgroundColor: "#e8f5e9",
          borderRadius: "4px",
        }}
        onClick={handleExpandToggle}
        data-attr="cap-ai:btn:toggle-similar-crs-section"
      >
        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
        <span style={{ fontWeight: 600 }}>Similar CRs from Dataset</span>
      </div>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <div
          style={{
            border: "1px solid #c8e6c9",
            borderTop: "none",
            borderRadius: "0 0 4px 4px",
            padding: "16px",
          }}
        >
          {/* Dataset and Field Selectors */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "16px",
              flexWrap: "wrap",
              alignItems: "flex-end",
            }}
          >
            <div style={{ maxWidth: "200px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
              >
                Dataset:
              </label>
              <select
                value={selectedDatasetId || ""}
                onChange={handleDatasetChange}
                data-attr="cap-ai:select:similar-crs-dataset"
                style={{
                  width: "200px",
                  padding: "6px 8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "13px",
                }}
                disabled={datasetsLoading}
              >
                <option value="">Select...</option>
                {datasets?.map((dataset) => (
                  <option key={dataset.id} value={dataset.id}>
                    {dataset.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ maxWidth: "180px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
              >
                Group By:
              </label>
              <select
                value={selectedOutputField}
                onChange={(e) => setSelectedOutputField(e.target.value)}
                data-attr="cap-ai:select:similar-crs-group-by"
                style={{
                  width: "180px",
                  padding: "6px 8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "13px",
                }}
              >
                <option value="">Select...</option>
                {outputFieldOptions.map((field) => (
                  <option key={field} value={field}>
                    {field}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ maxWidth: "80px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontWeight: 500,
                  fontSize: "13px",
                }}
              >
                Top N:
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={maxResults}
                onChange={handleMaxResultsChange}
                data-attr="cap-ai:input:similar-crs-max-results"
                style={{
                  width: "70px",
                  padding: "6px 8px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  fontSize: "13px",
                }}
              />
            </div>

            <Button
              variant="contained"
              size="small"
              onClick={handleFindSimilar}
              disabled={!selectedDatasetId || showLoading}
              data-attr="cap-ai:btn:find-similar-crs"
              startIcon={<FaSearch />}
              sx={{
                height: "34px",
                px: 2,
                minWidth: "120px",
                textTransform: "none",
              }}
            >
              {showLoading ? "Searching..." : "Find Similar"}
            </Button>
          </div>

          {/* Results Section */}
          {showLoading && (
            <Loading
              height="50px"
              width="50px"
              loading_message="Finding similar records..."
            />
          )}

          {similarCRsError && !showLoading && (
            <Alert severity="error">
              Error finding similar records: {similarCRsError.message}
            </Alert>
          )}

          {!showLoading && !similarCRsError && displayGroups && (
            <>
              <div
                style={{
                  marginBottom: "16px",
                  padding: "8px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "4px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#666" }}>
                  Found <strong>{cachedResults?.length || 0}</strong> similar
                  records
                </div>
                {cachedSearchQuery && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#999",
                      marginTop: "4px",
                    }}
                  >
                    Search query:{" "}
                    {cachedSearchQuery.length > 100
                      ? cachedSearchQuery.substring(0, 100) + "..."
                      : cachedSearchQuery}
                  </div>
                )}
              </div>

              {displayGroups.length > 0 ? (
                displayGroups.map((group, idx) => (
                  <SimilarCRGroup key={idx} group={group} />
                ))
              ) : (
                <Alert severity="info">
                  No similar records found in the selected dataset.
                </Alert>
              )}
            </>
          )}

          {!showLoading &&
            !similarCRsError &&
            !displayGroups &&
            selectedDatasetId && (
              <Alert severity="info">
                Click &quot;Find Similar&quot; to search for similar records.
              </Alert>
            )}
        </div>
      </Collapse>
    </div>
  );
};

SimilarCRsSection.propTypes = {
  automationRecordId: PropTypes.number.isRequired,
  groundTruthDatasetId: PropTypes.number,
  predictions: PropTypes.arrayOf(
    PropTypes.shape({
      model_output_name: PropTypes.string,
    }),
  ),
};

SimilarCRsSection.defaultProps = {
  groundTruthDatasetId: null,
  predictions: [],
};
